import { AgTechFileUploadButton, AgTechButton } from "@agtech/web/components/Buttons/AgTechButtons";
import AgTechGrid from "@agtech/web/components/Grids/AgTechGrid";
import { AgTechGridColumnMetadata } from "@agtech/web/components/Grids/Data/AgTechGridData";
import { AgTechGridSearchOption } from "@agtech/web/components/Grids/Features/AgTechGridSearching";
import { AgTechWarningIcon } from "@agtech/web/components/Icons/AgTechIcons";
import { AgTechStandardModal } from "@agtech/web/components/Portals/Modals/AgTechModal";
import { AgTechImportModalResultViewProps, AgTechImportGridItem, AgTechImportData } from "../common/AgTechImportTypes";
import { gridItems } from "@agtech/web/components/Grids/Data/AgTechGridProps";

export type AgTechSuccessfulImportResultsViewProps<TImportItem, TImportContext> = AgTechImportModalResultViewProps<TImportItem, TImportContext> & {
    importData: AgTechImportData<TImportItem, TImportContext>,
    onImportSubmitted: (importedItems: TImportItem[]) => Promise<void>
}

const AgTechSuccessfulImportResultsView = <TImportItem, TImportContext = {}>(
    props: AgTechSuccessfulImportResultsViewProps<TImportItem, TImportContext>
) => {
    let importResultsGridColumns = props.importProps.columns.map<AgTechGridColumnMetadata<AgTechImportGridItem<TImportItem>>>(column => ({
        header: column.columnName,
        value: gridItem => {
            let columnValidationErrors = gridItem.importItem.invalidColumns[column.columnName];
            
            if (columnValidationErrors) {
                return (
                    <div className='column-based stretched container'>
                        {columnValidationErrors.importedValue}
                        <AgTechWarningIcon classes='ml-auto' />
                    </div>
                )
            }
            else {
                return column.getItemValue(gridItem.importItem.importedItem, props.importData.context);
            }
        },
        itemBasedClasses: gridItem => gridItem.importItem.invalidColumns[column.columnName] ? 'invalid' : '',
        ...(column.getItemGridColumn ? column.getItemGridColumn() : {})
    }));

    importResultsGridColumns = [{
        header: 'Row #',
        value: gridItem => gridItem.importItem.itemRow,
        size: .5,
        classes: 'centered bg-slate-100'
    }, ...importResultsGridColumns];

    const importResultGridSearchOptions = props.importProps.columns.map<AgTechGridSearchOption<AgTechImportGridItem<TImportItem>>>(column => ({
        field: column.columnName,
        value: item => column.getItemValue(item.importItem.importedItem, props.importData.context)
    }));

    const importedItems = props.importResult.items;

    return (
        <AgTechStandardModal
            title='Import Results'
            details='Below are the results for this import. To complete this import, click the button below.'
            body={() => (
                <div className='row-based stretched fade-in-400 container'>
                    <AgTechGrid
                        header={{
                            title: `Imported ${props.content.importEntityPlural}`
                        }}
                        searchConfig={{
                            options: importResultGridSearchOptions
                        }}
                        data={{
                            itemQuery: gridItems(props.importResult.items.map<AgTechImportGridItem<TImportItem>>((item, index) => ({
                                id: index,
                                importItem: item
                            })))
                        }}
                        columns={importResultsGridColumns}
                        isRowEntityTheSame={() => true}
                    />
                </div>
            )}
            footer={() => (
                <div className='column-based-vertically-centered wide container'>
                    <AgTechFileUploadButton
                        text='Upload New File'
                        icon='fas fa-upload'
                        onFileUploaded={props.onImportAgain}
                        classes='py-2 px-5'
                    />
                    <AgTechButton
                        text={'Import ' + props.content.importEntityPlural}
                        icon='fas fa-plus'
                        action={async e => {
                            let wasImportConfirmed = await e.confirmations.askForConfirmation({
                                content: {
                                    header: `Are you sure you want to import ${importedItems.length} ${importedItems.length > 1 ? props.content.importEntityPlural : props.content.importEntitySingular}?`,
                                },
                                actions: {
                                    onConfirm: async () => {
                                        await props.onImportSubmitted(importedItems.map(e => e.importedItem));
                                        return true;
                                    }
                                }
                            });

                            return wasImportConfirmed;
                        }}
                        classes='button-highlight py-2 px-5 ml-auto'
                    />
                </div>
            )}
        />
    )
}

export default AgTechSuccessfulImportResultsView;